import React, { createContext, useReducer, useContext, ReactNode, useEffect } from 'react';

import { DestinationOption, GroupName } from '@lt/api/dist/methods/references/types';
import { DepartureOption } from '@lt/api/dist/types/departure';

import { FieldType, SearchType } from './types';
import { isEmpty } from 'lodash';
import { DatePickerState } from './components/DatePicker';
import { TouristsValue } from './components/FormTouristsPicker/types';
import { afterDestinationStep } from './utils';

const ActionType = {
  BACK_STEP: 'backStep',
  ADD_STEP: 'addStep',
  RESET_STEPS: 'resetSteps',
  UPDATE: 'update',
} as const;

type ActionType = typeof ActionType[keyof typeof ActionType];

type Action =
  | { type: typeof ActionType.BACK_STEP }
  | { type: typeof ActionType.RESET_STEPS }
  | { type: typeof ActionType.ADD_STEP; payload: string }
  | {
      type: typeof ActionType.UPDATE;
      payload: { isMobile: boolean; isWizard: boolean };
    };
type Dispatch = (action: Action) => void;
type State = {
  isWizard: boolean;
  isMobile: boolean;
  steps: string[];
};
type CigaretteProviderProps = {
  children: ReactNode;
  isMobile?: boolean;
  isWizard?: boolean;
  setIsWizardOpen?: (newValue: boolean) => void;
  initialOpenedField?: FieldType;
};

const CigaretteStateContext = createContext<{ state: State; dispatch: Dispatch } | undefined>(
  undefined
);

export type Values = {
  destination?: DestinationOption;
  datepicker?: DatePickerState;
  departure?: DepartureOption;
  tourists: TouristsValue;
};

export type PreviousSearchesOption = RequiredValues & {
  groupName: Extract<GroupName, 'previousSearches'>;
  searchType: SearchType;
  value?: number;
};

export type GroupedPreviousSearchesOption = {
  groupName: Extract<GroupName, 'previousSearches'>;
  options: PreviousSearchesOption[];
};

export type InitialValues = {
  destination: DestinationOption;
  datepicker?: DatePickerState;
  departure: DepartureOption;
  tourists: TouristsValue;
};

export type RequiredValues = Required<Values>;

export const addStep = (payload: string) => ({
  type: ActionType.ADD_STEP,
  payload,
});

export const backStep = () => ({
  type: ActionType.BACK_STEP,
});

export const resetSteps = () => ({
  type: ActionType.RESET_STEPS,
});

export const update = (payload: { isMobile: boolean; isWizard: boolean }) => ({
  type: ActionType.UPDATE,
  payload,
});

function cigaretteReducer(state: State, action: Action) {
  switch (action.type) {
    case ActionType.BACK_STEP: {
      return {
        ...state,
        steps: [...state.steps.slice(0, state.steps.length - 1)],
      };
    }
    case ActionType.ADD_STEP: {
      return { ...state, steps: [...state.steps, action.payload] };
    }
    case ActionType.RESET_STEPS: {
      return { ...state, steps: [] };
    }
    case ActionType.UPDATE: {
      return {
        ...state,
        isMobile: action.payload.isMobile,
        isWizard: action.payload.isWizard,
      };
    }
    default: {
      return state;
    }
  }
}

function CigaretteProvider({
  children,
  isMobile = false,
  isWizard = false,
  setIsWizardOpen,
  initialOpenedField,
}: CigaretteProviderProps) {
  const [state, dispatch] = useReducer(cigaretteReducer, {
    steps: [],
    isMobile,
    isWizard,
  });
  const value = { state, dispatch };

  useEffect(() => {
    dispatch(update({ isMobile, isWizard }));
  }, [isMobile, isWizard]);

  useEffect(() => {
    if (!setIsWizardOpen) return;

    if (isEmpty(state?.steps)) {
      setIsWizardOpen(false);
    }
    if (afterDestinationStep(state?.steps)) {
      setIsWizardOpen(true);
    }
  }, [state?.steps, setIsWizardOpen]);

  useEffect(() => {
    if (!isWizard || !initialOpenedField) return;

    dispatch(addStep(initialOpenedField));
  }, [initialOpenedField, isWizard]);

  return <CigaretteStateContext.Provider value={value}>{children}</CigaretteStateContext.Provider>;
}

function useCigarette() {
  const context = useContext(CigaretteStateContext);
  if (context === undefined) {
    throw new Error('useCigarette must be used within a CigaretteProvider');
  }
  return context;
}

export { CigaretteProvider, useCigarette };
